<template>
  <div class="product" :class="classes">
    <div class="image-wrapper">
      <Media
        v-if="groupedProductImage"
        :images="{
          blurry: groupedProductImage.blurry,
          smallMedia: groupedProductImage.smallMedia,
          altText: groupedProductImage.altText,
        }"
      />
    </div>
    <div class="text">
      <div class="info">
        <a :href="product.onlineStoreUrl" class="product-link">
          <h3 class="title">
            {{ product.title }}
          </h3>
        </a>
        <p class="price">
          {{
            $filters.toCurrency(
              product.priceRange.minVariantPrice.amount,
              product.priceRange.minVariantPrice.currencyCode,
              0
            )
          }}
        </p>
      </div>
      <div class="actions">
        <label class="field field--select">
          <div class="input input--select">
            <select class="input__element" @change="handleVariantChange">
              <option disabled selected>Size</option>
              <option
                v-for="variant in dropdownVariants"
                :key="variant.value"
                :value="variant.value"
              >
                {{ variant.label }}
              </option>
            </select>
          </div>
        </label>
        <Button :disabled="buttonStatus" @click="handleAddToBagClick">
          {{ buttonText }}
        </Button>
      </div>
    </div>
  </div>
</template>

<script>
import { unwrapNodes } from "@/helpers/graphql";
import { required, helpers } from "@vuelidate/validators";
import useVuelidate from "@vuelidate/core";
import Button from "@/components/ButtonV3.vue";
import { mapActions } from "vuex";
import Media from "@/components/Media.vue";

export default {
  name: "SetProduct",
  components: {
    Button,
    Media,
  },
  props: {
    product: {
      type: Object,
      required: true,
    },
    orientation: {
      type: String,
      default: "landscape",
      validator: function (value) {
        return ["landscape", "portrait"].includes(value);
      },
    },
  },
  emits: ["variant-selected"],
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      selectedVariant: "",
    };
  },
  computed: {
    classes() {
      return {
        "product--portrait": this.orientation === "portrait",
      };
    },
    dropdownVariants() {
      return this.variants.map((variant) => {
        return {
          value: variant.id,
          label: variant.title,
          disabled: !variant.availableForSale,
        };
      });
    },
    selectedVariantTitle() {
      return this.variants.filter((variant) => {
        return variant.id === this.selectedVariant;
      })[0]?.title;
    },
    groupedProductImage() {
      const unwrappedImageNodes = unwrapNodes(this.product.images?.edges);
      const unwrappedCampaignNodes = unwrapNodes(
        this.product.campaignImages?.references.edges
      );

      const image = unwrappedCampaignNodes
        ? unwrappedCampaignNodes[0].image
        : unwrappedImageNodes
        ? unwrappedImageNodes[0]
        : null;

      return image;
    },
    variants() {
      return unwrapNodes(this.product.variants?.edges) || [];
    },
    buttonStatus() {
      if (
        this.selectedVariant?.quantityAvailable <= 0 &&
        !this.selectedVariant.availableForSale
      ) {
        return true;
      }

      return false;
    },
    buttonText() {
      if (
        this.selectedVariant?.quantityAvailable <= 0 &&
        !this.selectedVariant.availableForSale
      ) {
        return "Sold Out";
      }

      return "Add";
    },
  },
  methods: {
    ...mapActions("cart", ["addCartItem", "setCartOpen"]),
    handleVariantChange({ target }) {
      const variant = this.variants.find((variant) => {
        return variant.id === target.value;
      });

      this.selectedVariant = variant;
    },
    handleAddToBagClick() {
      this.addCartItem({
        productId: this.product.id,
        variantId: this.selectedVariant.id,
      });
      this.setCartOpen(true);
    },
  },
  validations() {
    return {
      selectedVariant: {
        required: helpers.withMessage("Please select a variant", required),
      },
    };
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/css/components/form";

.product {
  display: grid;
  grid-template-columns: 2fr 3fr;
  gap: 20px;

  &--portrait {
    gap: 12px;
    grid-template-columns: 1fr;
  }
}

.image-wrapper {
  display: block;

  img {
    object-fit: contain;
    height: 100%;
    object-position: center center;
    display: block;
    width: 100%;
  }

  :deep(.media) {
    aspect-ratio: 4/5;
  }
}

.product-link {
  text-decoration: none;
}

.text {
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  .actions {
    margin-top: 20px;
    display: flex;
    gap: 20px;

    & > * {
      flex: 1 0 auto;
    }

    .field {
      margin-bottom: 0;

      .input {
        margin-bottom: 0;
      }
    }
  }

  .title {
    @include utility-body(15px);
    margin-bottom: 2px;
  }

  .price {
    @include utility-body(15px);
  }
}
</style>
